import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './store';
import { BrowserRouter as Router } from 'react-router-dom';
import { installCellRoutingInterceptor, hydrateFromStoredToken } from './cellRouting';
import { installTimezoneShim } from './utils/datetime';

// Every date shown in the UI honors the user's preferred timezone
// (Settings > Preferences): toLocale* calls without an explicit
// timeZone get the preference injected globally.
installTimezoneShim();

// Multi-cell: transparently follow a `wrong_cell` 409 to the user's own cell,
// and proactively resolve a returning user's cell from their stored token.
installCellRoutingInterceptor();
hydrateFromStoredToken();

// ── Kill the benign "ResizeObserver loop …" error at its SOURCE ──────────
// The browser emits this warning when a ResizeObserver callback mutates layout
// and re-triggers itself within the same delivery cycle. ReactFlow does this
// constantly while measuring nodes and auto-fitting the relationship graph
// (dragging the strength slider rebuilds every node → re-measure → re-layout).
// It is non-actionable, but CRA's dev overlay promotes any window error to a
// fatal modal.
//
// Rather than race the overlay's error listener (ordering isn't guaranteed, so
// stopImmediatePropagation can arrive too late), we wrap ResizeObserver so each
// callback is deferred to the next animation frame. That moves every delivery
// cycle into its own frame, so the observer can never re-enter the frame that
// triggered it and the loop is never detected — for ReactFlow or anything else.
const NativeResizeObserver = window.ResizeObserver;
if (typeof NativeResizeObserver === 'function') {
  window.ResizeObserver = class BatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        // rAF defers delivery to the next frame; wrapping the call in a
        // try/catch keeps a throwing consumer callback from surfacing as an
        // unhandled rejection off the frame tick.
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
          }
        });
      });
    }
  };
}

// Backstop: swallow the message if it ever still reaches the window (e.g. from a
// third-party polyfilled observer we don't wrap). Registered in the CAPTURE
// phase; stopImmediatePropagation then keeps the dev overlay from seeing it.
const RESIZE_OBSERVER_ERR =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
const isResizeObserverNoise = (msg) =>
  typeof msg === 'string' && RESIZE_OBSERVER_ERR.test(msg);
window.addEventListener('error', (e) => {
  if (isResizeObserverNoise(e?.message) || isResizeObserverNoise(e?.error?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  if (isResizeObserverNoise(e?.reason?.message) || isResizeObserverNoise(e?.reason)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
  <Provider store={store}>
    <Router>
      <App />
    </Router>
  </Provider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
